import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { adminSeasonsAPI } from '../services/adminApi';
import { getAdminTenantId } from '../utils/tenantHelper';
import {
  buildSeasonOptionsFromPhases,
  filterPhasesForSeason,
  normalizeAdminPhasesList,
  phaseParentSeasonId,
  resolveDefaultSeasonPhaseIds,
} from '../utils/adminPhaseDefaults';

/**
 * Shared Season → Phase filter state with live-phase default.
 *
 * @param {{
 *   allowAll?: boolean,
 *   autoDefault?: boolean,
 *   enabled?: boolean,
 *   preferredPhaseId?: string,
 * }} opts
 */
export function useAdminSeasonPhaseFilter(opts = {}) {
  const {
    allowAll = true,
    autoDefault = true,
    enabled = true,
    preferredPhaseId = '',
  } = opts;

  const tenantId = getAdminTenantId();
  const [campaignId, setCampaignId] = useState('');
  const [seasonId, setSeasonId] = useState('');
  const didDefault = useRef(false);
  const preferredRef = useRef(preferredPhaseId);

  useEffect(() => {
    preferredRef.current = preferredPhaseId;
  }, [preferredPhaseId]);

  // Reset default when tenant changes or filter becomes enabled again
  useEffect(() => {
    didDefault.current = false;
    setCampaignId('');
    setSeasonId('');
  }, [tenantId]);

  useEffect(() => {
    if (enabled) {
      didDefault.current = false;
    }
  }, [enabled]);

  const { data: seasonsRes, isLoading } = useQuery(
    ['admin-seasons', tenantId],
    () => adminSeasonsAPI.getAll(),
    { enabled: !!enabled && !!tenantId, staleTime: 60 * 1000, retry: false }
  );

  const allPhases = useMemo(
    () => normalizeAdminPhasesList(seasonsRes?.data ?? seasonsRes),
    [seasonsRes]
  );

  const seasonOptions = useMemo(() => buildSeasonOptionsFromPhases(allPhases), [allPhases]);

  const phaseOptions = useMemo(
    () => filterPhasesForSeason(allPhases, campaignId),
    [allPhases, campaignId]
  );

  useEffect(() => {
    if (!autoDefault || didDefault.current || !allPhases.length) return;
    const preferred = preferredRef.current || '';
    const resolved = resolveDefaultSeasonPhaseIds(allPhases, preferred);
    if (!resolved.seasonId && allowAll) {
      didDefault.current = true;
      return;
    }
    if (resolved.seasonId) {
      setCampaignId(resolved.campaignId || '');
      setSeasonId(resolved.seasonId);
      didDefault.current = true;
    }
  }, [allPhases, autoDefault, allowAll]);

  // If preferredPhaseId arrives later (handoff), apply once when still unset or override empty
  useEffect(() => {
    if (!preferredPhaseId || !allPhases.length) return;
    const hit = allPhases.find((p) => String(p.id) === String(preferredPhaseId));
    if (!hit) return;
    setSeasonId(String(hit.id));
    setCampaignId(phaseParentSeasonId(hit));
    didDefault.current = true;
  }, [preferredPhaseId, allPhases]);

  const onCampaignChange = (nextCampaignId) => {
    const cid = nextCampaignId || '';
    setCampaignId(cid);
    if (!cid) {
      // Keep current phase if still valid among all; else clear or re-default within all
      return;
    }
    const inSeason = filterPhasesForSeason(allPhases, cid);
    if (seasonId && inSeason.some((p) => String(p.id) === String(seasonId))) {
      return;
    }
    const resolved = resolveDefaultSeasonPhaseIds(inSeason);
    setSeasonId(resolved.seasonId || (allowAll ? '' : ''));
  };

  const onPhaseChange = (nextPhaseId) => {
    const sid = nextPhaseId || '';
    setSeasonId(sid);
    if (!sid) return;
    const hit = allPhases.find((p) => String(p.id) === String(sid));
    if (hit) {
      const parent = phaseParentSeasonId(hit);
      if (parent) setCampaignId(parent);
    }
  };

  return {
    campaignId,
    seasonId,
    setCampaignId,
    setSeasonId,
    onCampaignChange,
    onPhaseChange,
    seasonOptions,
    phaseOptions,
    allPhases,
    isLoading,
    allowAll,
  };
}
